import Cookies from "js-cookie";
import React from "react";

import { FALLBACK_NUMBER, FALLBACK_NUMBER_HREF } from "../shared/helpers/ellipsis";
import { ISO_DATE_FORMAT, UK_DATE_FORMAT } from "../const/DateFormatConsts";
import { INITIAL_STATE, SEARCH_STATUS, SORT_OPTIONS, URL_PARTS } from "../const/SearchConsts";

import dayjs from "../shared/helpers/dayjs";
import { gaEvent } from "../shared/helpers/gtm_tracking";

import FrontendAPI from "../scripts/api/search";

import SearchResults from "../components/search/SearchResults";

import SearchHeader from "./SearchHeader";
import SearchNotification from "./SearchNotification";

const PAGE_TYPE = "Search Page";

const { search } = new FrontendAPI();

function compareOrderFn(first, second) {
  const x = URL_PARTS[first]["order"];
  const y = URL_PARTS[second]["order"];
  const delta = this === "desc"
    ? y - x
    : x - y;

  return Math.max(-1, Math.min(1, delta));
}

const sortResults = (sort, results) => {
  const { compare } = SORT_OPTIONS.find(({ option }) => option === sort) || {};

  return compare ? compare(results) : results;
};

const sortUrlKeys = (obj) => {
  const keys = Object.keys(obj).filter(key => key !== "region");

  return keys.sort(compareOrderFn);
};

async function fetchResults(params) {
  const { error = null, region_name: regionName, results = [] } = await search(params);

  return {
    count: error ? 0 : results.length,
    error,
    regionName,
    results: sortResults(params.sort, results),
    status: error ? SEARCH_STATUS.ERROR : SEARCH_STATUS.SUCCESS
  };
}

function getRegionOrPlaceName(params) {
  const { place, region } = params;
  const name = region || place || "";

  return name.split(region ? "/" : ",")[region ? 1 : 0];
}

function getUrlParam(parts, part) {
  const index = parts.indexOf(part) + 1;
  let value;

  if (index > 0) {
    const { length } = URL_PARTS[part];
    const subpart = parts.slice(index, index + length);

    if (subpart.length === length) {
      if (length === 1) {
        value = subpart[0];
      } else if (part === "date") {
        value = subpart.join("-");
      } else if (part === "place") {
        value = subpart.map(item => decodeURIComponent(item));
      }
    }
  }

  return value;
}

function getUrlRegion(parts) {
  let region = null;

  const special = parts.find(part => /-golf-(break|holiday)s$/.test(part));

  if (special) {
    return special;
  }

  const index = parts.findIndex(part => /^golf-(break|holiday)s$/.test(part));

  if (index !== -1) {
    region = `${parts[index]}/${parts[index + 1]}`;
  }

  return region;
}

function getUrlParams() {
  const keys = Object.keys(URL_PARTS);
  const parts = location.pathname.split("/");
  const params = {};
  const region = getUrlRegion(parts);

  if (region) {
    params.region = region;
  }

  keys.forEach(key => {
    const part = getUrlParam(parts, key);
    const { re } = URL_PARTS[key];

    if (part) {
      params[key] = part;
    }

    if (re && !re.test(params[key])) {
      delete params[key];
    }
  });

  return params;
}

function setUrlParams({ name, value }) {
  const params = getUrlParams();

  if (!value) {
    delete params[name];
  } else {
    params[name] = value;
  }

  return [params["region"]].concat(sortUrlKeys(params)
    .map(key => {
      let val = params[key];

      if (key === "date") {
        val = val.replace(/-/g, "/");
      }

      return `${key}/${val}`;
    }))
    .join("/");
}

function onFilterChange(e, self = this) {
  if (!self) {
    return;
  }

  const { form } = e.target || self.textInput;
  const { name } = self.props;
  const value = e.target ? e.target.value : e;

  if (e.target) {
    self.setState({
      selected: value
    });
  }

  const pathname = setUrlParams({ name, value });
  const label = name.charAt(0).toUpperCase() + name.slice(1);

  gaEvent("Search Page", "Search Filter", label);
  history.replaceState("", "", `${location.origin}/${pathname}`);
  form.dispatchEvent(new Event("submit"));
}

function checkCookieDate() {
  const arrivalDate = Cookies.get("arrival_date");

  if (dayjs(arrivalDate, UK_DATE_FORMAT).isValid()) {
    onFilterChange(arrivalDate);
  }
}

function parseNights(value) {
  const array = value.split("-");

  if (array.length === 1) {
    array.push(value.endsWith("+") ? "99" : array[0]);
  }

  return {
    nights: value,
    nights_min: parseInt(array[0], 10),
    nights_max: parseInt(array[1], 10)
  };
}

function parseRounds(value) {
  const array = value.split("-");

  if (array.length === 1) {
    if (array[0] === "unlimited") {
      array[0] = -1;
    }

    array.push(array[0]);
  }

  return {
    rounds: value,
    rounds_min: parseInt(array[0], 10),
    rounds_max: parseInt(array[1], 10)
  };
}

function parseUrlParams() {
  const urlParams = getUrlParams();
  const params = {};
  let value = urlParams["region"];

  if (value) {
    Object.assign(params, { region: value });
  }

  value = urlParams["date"];
  if (value) {
    const date = dayjs(value, UK_DATE_FORMAT);

    if (date.isValid()) {
      Object.assign(params, {
        date: date.format(ISO_DATE_FORMAT)
      });
    }
  }

  value = urlParams["nights"];
  if (value) {
    Object.assign(params, parseNights(value));
  }

  value = urlParams["place"];
  if (value) {
    Object.assign(params, {
      bounding_box: value[1],
      place: value[0]
    });
  }

  value = urlParams["rounds"];
  if (value) {
    Object.assign(params, parseRounds(value));
  }

  value = urlParams["sort"];
  if (value) {
    Object.assign(params, {
      sort: value
    });
  }

  return params;
}

function renderForm({ /* params, */setIsSearching, setParams }) {
  return (
    <form
      onSubmit={e => {
        e.preventDefault();

        setParams(parseUrlParams());
        setIsSearching(true);
      }}
    >
      {/* <SearchFilters
        onFilterChange={onFilterChange}
        {...params}
      /> */}
    </form>
  );
}

function useSearch() {
  const [params, setParams] = React.useState(parseUrlParams());
  const [isSearching, setIsSearching] = React.useState(!!Object.keys(params).length);
  const [state, setState] = React.useState(INITIAL_STATE);
  const { region = null } = params;
  const place = getRegionOrPlaceName(params);
  const type = region && !region.match(/golf-breaks/)
    ? "golf holiday"
    : "golf break";
  const extras = { location: place, type };

  React.useEffect(() => {
    async function resultsFetcher() {
      setState({
        ...INITIAL_STATE,
        ...extras,
        status: SEARCH_STATUS.LOADING
      });

      const results = await fetchResults(params);
      const { regionName } = results;

      if (regionName) {
        extras.location = regionName;
      }

      setState({
        ...INITIAL_STATE,
        ...extras,
        ...results
      });
      setIsSearching(false);
    }

    if (isSearching) {
      resultsFetcher();
    }
  }, [isSearching]);

  return [{ params, state }, setIsSearching, setParams];
}

function SearchProvider() {
  /* check cookie for previously set arrival date and update URL. Nasty */
  checkCookieDate();

  const [{ params, state }, setIsSearching, setParams] = useSearch();
  const date = params.date ? dayjs(params.date) : null;

  return (
    <React.Fragment>
      <SearchNotification
        container="[data-component=notification] p"
        results={state.results}
        searchDataLoading={state.status === SEARCH_STATUS.LOADING}
        silentFail
      />
      <div className="wrapper">
        <SearchHeader {...state} />
        {renderForm({ params, setIsSearching, setParams })}
      </div>
      {state.status !== SEARCH_STATUS.LOADING && <SearchResults
        fallbackNumber={FALLBACK_NUMBER}
        fallbackNumberHref={FALLBACK_NUMBER_HREF}
        pageType={PAGE_TYPE}
        regionName={state.location}
        results={state.results}
        resultsCount={state.count}
        params={{
          ...params,
          date,
          place: state.location
        }}
      />}
    </React.Fragment>
  );
}

export default SearchProvider;



// WEBPACK FOOTER //
// ./providers/SearchProvider.jsx